const FALLBACK_COMMENT_COLOR = '#2563EB';

const FALLBACK_UNKNOWN_DISPLAY = 'Unknown user';
const MAX_DISPLAY_NAME_LENGTH = 20;

export type BasicUserInfo = {
  name?: string | null;
  email?: string | null;
};

export type CommentAuthorVisual = {
  displayName: string;
  fallbackText: string;
  color: string;
};

const extractInitials = (value: string): string => {
  const parts = value.split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) {
    return value.slice(0, 2).toUpperCase() || 'U';
  }
  return parts
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'U';
};

const truncateDisplayName = (value: string): string => {
  if (value.length <= MAX_DISPLAY_NAME_LENGTH) {
    return value;
  }
  return `${value.slice(0, MAX_DISPLAY_NAME_LENGTH - 1)}…`;
};

export const buildDisplayName = (
  authorId: string | null | undefined,
  userInfo?: BasicUserInfo | null,
): string => {
  const id = authorId ? authorId.trim() : '';
  const name = userInfo?.name?.trim();
  if (name) {
    return name;
  }
  const email = userInfo?.email?.trim();
  if (email) {
    return email;
  }
  if (id) {
    return `User ${id}`;
  }
  return FALLBACK_UNKNOWN_DISPLAY;
};

export const resolveCommentAuthorVisual = (
  authorId: string | null | undefined,
  userInfo?: BasicUserInfo | null,
  assignedColor?: string | null,
): CommentAuthorVisual => {
  const displayName = truncateDisplayName(buildDisplayName(authorId, userInfo));
  const baseColor = normalizeColor(assignedColor) ?? FALLBACK_COMMENT_COLOR;
  const color = lightenColor(baseColor, 0.35);
  return {
    displayName,
    fallbackText: extractInitials(displayName),
    color,
  };
};

const normalizeColor = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const hex = value.trim();
  if (!/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }
  return hex.startsWith('#') ? hex.toUpperCase() : `#${hex.toUpperCase()}`;
};

const lightenColor = (hex: string, factor = 0.35): string => {
  const stripped = hex.replace('#', '');
  const normalized = stripped.length === 3 ? stripped.split('').map((c) => c + c).join('') : stripped;
  const base = parseInt(normalized, 16);
  const adjust = (shift: number) => {
    const channel = (base >> shift) & 0xff;
    return Math.min(255, Math.round(channel + (255 - channel) * factor));
  };
  const r = adjust(16);
  const g = adjust(8);
  const b = adjust(0);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`.toUpperCase();
};
